import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { APP_NAME } from "@/lib/brand";
import { AuthBackdrop } from "@/components/layout/AuthBackdrop";
import { GuestLogin } from "@/components/layout/GuestLogin";
import { guestSignupOn } from "@/lib/guest";

const UNVERIFIED = "Check your email to finish signing up";

const CheckEmail = ({ email, onBack }) => {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try { await api.post("/auth/resend-verification", { email }); setMsg("Sent again. Check your inbox (and spam folder)."); } catch (e) { setMsg(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col" data-testid="check-email-box">
      <div className="auth-brand serif">{APP_NAME}</div>
      <h2 className="serif text-xl text-neutral-900 mt-6">{UNVERIFIED}</h2>
      <p className="text-sm text-neutral-600 mt-3">We sent a link to <b>{email}</b>. Open it to create your password. It works for 24 hours.</p>
      {msg && <p className="auth-success mt-4" data-testid="resend-message">{msg}</p>}
      <button type="button" className="btn-ink w-full mt-6" disabled={busy} onClick={resend} data-testid="resend-verification-button">
        {busy ? <Loader2 className="animate-spin mx-auto" size={18} /> : "Resend email"}
      </button>
      <button type="button" className="auth-link mt-5 self-start" onClick={onBack} data-testid="check-email-back-button">Back to sign in</button>
    </div>
  );
};

const googleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function LoginPage() {
  const { user, setUser } = useAuth();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("forgot") ? "forgot" : params.get("guest") !== null ? "guest" : "login");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [check, setCheck] = useState(""); // email waiting for verification
  const [signupOn, setSignupOn] = useState(false);
  useEffect(() => { guestSignupOn().then(setSignupOn); }, []);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "register") {
        await api.post("/auth/register", { name: form.name, email: form.email });
        setCheck(form.email);
      } else {
        setUser((await api.post("/auth/login", { email: form.email, password: form.password })).data);
      }
    } catch (err) {
      if (err.response?.data?.detail === UNVERIFIED) setCheck(form.email);
      else setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const field = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });
  const go = (m) => { setMode(m); setError(""); };

  if (check) {
    return <AuthBackdrop testid="check-email-page"><CheckEmail email={check} onBack={() => { setCheck(""); go("login"); }} /></AuthBackdrop>;
  }

  if (mode === "guest") {
    return <AuthBackdrop testid="guest-login-page"><GuestLogin initial={params.get("guest") || ""} signupOn={signupOn} onBack={() => go("login")} /></AuthBackdrop>;
  }

  if (mode === "forgot") {
    const ask = async (e) => {
      e.preventDefault();
      setBusy(true);
      setError("");
      try {
        setNotice((await api.post("/auth/forgot-password", { email: form.email })).data.message);
      } catch (err) {
        setError(errMsg(err));
      } finally {
        setBusy(false);
      }
    };
    return (
      <AuthBackdrop testid="forgot-page">
        <form className="flex flex-col" onSubmit={ask} data-testid="forgot-form">
          <div className="auth-brand serif" data-testid="login-brand">{APP_NAME}</div>
          <h2 className="serif text-xl text-neutral-900 mt-6">Forgot your password?</h2>
          {notice ? (
            <p className="text-sm text-neutral-700 mt-3" data-testid="forgot-message">{notice}</p>
          ) : (
            <>
              <p className="text-sm text-neutral-600 mt-2 mb-5">Enter your email and we'll send you a link to choose a new one.</p>
              <input className="login-input" type="email" placeholder="Email" required autoComplete="email" {...field("email")} data-testid="forgot-email-input" />
              {error && <p className="text-sm text-red-600 mb-3" data-testid="forgot-error">{error}</p>}
              <button className="btn-ink w-full" disabled={busy} data-testid="forgot-submit-button">
                {busy ? <Loader2 className="animate-spin mx-auto" size={18} /> : "Send reset link"}
              </button>
            </>
          )}
          <button type="button" className="auth-link mt-5 self-start" onClick={() => { setNotice(""); go("login"); }} data-testid="forgot-back-button">Back to sign in</button>
        </form>
      </AuthBackdrop>
    );
  }

  return (
    <AuthBackdrop testid="login-page">
      <form className="flex flex-col" onSubmit={submit} data-testid="login-form">
        <div className="auth-brand serif" data-testid="login-brand">{APP_NAME}</div>
        <p className="text-sm text-neutral-600 mt-2 mb-7 text-center">{mode === "login" ? "Sign in to the family photo book." : "Create your account."}</p>
        {mode === "login" && params.get("reset") && <p className="auth-success" data-testid="password-updated-notice">Password updated. Sign in with your new password.</p>}
        {mode === "register" && (
          <input className="login-input" placeholder="Your name" required {...field("name")} data-testid="register-name-input" />
        )}
        <input className="login-input" type="email" placeholder="Email" required autoComplete="email" {...field("email")} data-testid="login-email-input" />
        {mode === "login" && (
          <input className="login-input" type="password" placeholder="Password" required autoComplete="current-password" {...field("password")} data-testid="login-password-input" />
        )}
        {mode === "register" && <p className="text-xs text-neutral-500 mb-4" data-testid="register-hint">We'll email you a link to create your password.</p>}
        {error && <p className="text-sm text-red-600 mb-3" data-testid="login-error">{error}</p>}
        <button className="btn-ink w-full" disabled={busy} data-testid="login-submit-button">
          {busy ? <Loader2 className="animate-spin mx-auto" size={18} /> : mode === "login" ? "Sign in" : "Continue"}
        </button>
        <div className="login-or"><span>or</span></div>
        <button type="button" className="btn-outline w-full" onClick={googleLogin} data-testid="google-login-button">
          Continue with Google
        </button>
        <div className="flex justify-between gap-4 mt-6">
          <button type="button" className="auth-link" onClick={() => go(mode === "login" ? "register" : "login")} data-testid="toggle-auth-mode">
            {mode === "login" ? "Create an account" : "Already have an account? Sign in"}
          </button>
          {mode === "login" && <button type="button" className="auth-link" onClick={() => go("forgot")} data-testid="forgot-password-link">Forgot password?</button>}
          {mode === "login" && <button type="button" className="auth-link" onClick={() => go("guest")} data-testid="guest-login-link">{signupOn ? "Continue as guest" : "Have a guest username from the family? Enter it here"}</button>}
          {mode === "login" && !signupOn && <p className="text-xs text-neutral-500 mt-1" data-testid="guest-closed-hint">New guest sign-up is currently closed. Ask the family for an invite.</p>}
        </div>
      </form>
    </AuthBackdrop>
  );
}
